import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  IAutoPipeRoutingCostSettings,
  IDeviceCategoryEnum,
} from "@multiconsult-group/multiplacer-core";
import { ZVec3M } from "@multiconsult-group/multiplacer-core";
import type { IRootState } from "../app/store";
import type { IPrimarySystemKind } from "../systemsConstants";
import type { z } from "zod";

export type IAutoPipeRoutingSystemType = Extract<
  IPrimarySystemKind,
  "SUPPLY_AIR" | "EXHAUST_AIR" | "FIRE_PROTECTION"
>;

export type IAutoPipeRoutingConnectionPoint = z.infer<typeof ZVec3M>;
export type IAutoPipeRoutingWizardStep = 1 | 2 | 3 | 4 | 5 | 6;

export interface IPipeRoutingSliceState {
  autoPipeRoutingModalOpen: boolean;
  autoPipeRoutingPickingConnectionPointInViewer: boolean;
  autoPipeRoutingWizardStep: IAutoPipeRoutingWizardStep;
  selectedAutoPipeRoutingSystemType: IAutoPipeRoutingSystemType | null;
  autoPipeRoutingPipeElevationsMmAboveFloorBySystem: Partial<
    Record<
      IAutoPipeRoutingSystemType,
      {
        xDirectionMmAboveFloor: number;
        yDirectionMmAboveFloor: number;
      }
    >
  >;
  autoPipeRoutingConnectionPoints: IAutoPipeRoutingConnectionPoint[];
  autoPipeRoutingPipeSeriesName: string | null;
  autoPipeRoutingPipeDimension: number | null;
  autoPipeRoutingCostSettings: IAutoPipeRoutingCostSettings;
  autoPipeRoutingRunState: {
    isPreparing: boolean;
    isRunning: boolean;
    currentIteration: number;
    totalIterations: number;
  };
}

export const AUTO_PIPE_ROUTING_SYSTEM_TYPE_TO_DEVICE_CATEGORY: Record<
  IAutoPipeRoutingSystemType,
  IDeviceCategoryEnum
> = {
  SUPPLY_AIR: "SUPPLY_AIR_DEVICE",
  EXHAUST_AIR: "RETURN_AIR_DEVICE",
  FIRE_PROTECTION: "FIRE_SUPPRESSION_DEVICE",
};

const DEFAULT_AUTO_PIPE_ROUTING_PIPE_ELEVATIONS_MM_ABOVE_FLOOR = {
  xDirectionMmAboveFloor: 3400,
  yDirectionMmAboveFloor: 3800,
};
const DEFAULT_AUTO_PIPE_ROUTING_COST_SETTINGS: IAutoPipeRoutingCostSettings = {
  roomCrossingPenaltyM: 3.0,
  corridorRoomFactor: 0.1,
  blockedRoomFactor: 100,
  verticalPenaltyFactor: 3,
  perimeterPenaltyFactor: 1,
};

const initialState: IPipeRoutingSliceState = {
  autoPipeRoutingModalOpen: false,
  autoPipeRoutingPickingConnectionPointInViewer: false,
  autoPipeRoutingWizardStep: 1,
  selectedAutoPipeRoutingSystemType: null,
  autoPipeRoutingPipeElevationsMmAboveFloorBySystem: {
    SUPPLY_AIR: DEFAULT_AUTO_PIPE_ROUTING_PIPE_ELEVATIONS_MM_ABOVE_FLOOR,
    EXHAUST_AIR: DEFAULT_AUTO_PIPE_ROUTING_PIPE_ELEVATIONS_MM_ABOVE_FLOOR,
    FIRE_PROTECTION: DEFAULT_AUTO_PIPE_ROUTING_PIPE_ELEVATIONS_MM_ABOVE_FLOOR,
  },
  autoPipeRoutingConnectionPoints: [],
  autoPipeRoutingPipeSeriesName: null,
  autoPipeRoutingPipeDimension: null,
  autoPipeRoutingCostSettings: DEFAULT_AUTO_PIPE_ROUTING_COST_SETTINGS,
  autoPipeRoutingRunState: {
    isPreparing: false,
    isRunning: false,
    currentIteration: 0,
    totalIterations: 0,
  },
};

export const pipeRoutingSlice = createSlice({
  name: "pipeRouting",
  initialState,
  reducers: {
    setAutoPipeRoutingModalOpenInRedux(state, action: PayloadAction<boolean>) {
      state.autoPipeRoutingModalOpen = action.payload;
    },
    setAutoPipeRoutingPickingConnectionPointInViewerInRedux(state, action: PayloadAction<boolean>) {
      state.autoPipeRoutingPickingConnectionPointInViewer = action.payload;
    },
    setAutoPipeRoutingWizardStepInRedux(state, action: PayloadAction<IAutoPipeRoutingWizardStep>) {
      state.autoPipeRoutingWizardStep = action.payload;
    },
    setSelectedAutoPipeRoutingSystemTypeInRedux(
      state,
      action: PayloadAction<IAutoPipeRoutingSystemType | null>
    ) {
      state.selectedAutoPipeRoutingSystemType = action.payload;
    },
    clearSelectedAutoPipeRoutingSystemTypeInRedux(state) {
      state.selectedAutoPipeRoutingSystemType = null;
    },
    setAutoPipeRoutingPipeElevationsMmAboveFloorForSystemInRedux(
      state,
      action: PayloadAction<{
        systemType: IAutoPipeRoutingSystemType;
        pipeElevationsMmAboveFloor: {
          xDirectionMmAboveFloor: number;
          yDirectionMmAboveFloor: number;
        };
      }>
    ) {
      state.autoPipeRoutingPipeElevationsMmAboveFloorBySystem[action.payload.systemType] =
        action.payload.pipeElevationsMmAboveFloor;
    },
    setAutoPipeRoutingConnectionPointInRedux(
      state,
      action: PayloadAction<IAutoPipeRoutingConnectionPoint[]>
    ) {
      state.autoPipeRoutingConnectionPoints = action.payload;
    },
    addAutoPipeRoutingConnectionPointInRedux(
      state,
      action: PayloadAction<IAutoPipeRoutingConnectionPoint>
    ) {
      state.autoPipeRoutingConnectionPoints.push(action.payload);
    },
    setAutoPipeRoutingPipeSeriesNameInRedux(state, action: PayloadAction<string | null>) {
      state.autoPipeRoutingPipeSeriesName = action.payload;
    },
    setAutoPipeRoutingPipeDimensionInRedux(state, action: PayloadAction<number | null>) {
      state.autoPipeRoutingPipeDimension = action.payload;
    },
    setAutoPipeRoutingRoomCrossingPenaltyMInRedux(state, action: PayloadAction<number>) {
      state.autoPipeRoutingCostSettings.roomCrossingPenaltyM = action.payload;
    },
    setAutoPipeRoutingCorridorRoomFactorInRedux(state, action: PayloadAction<number>) {
      state.autoPipeRoutingCostSettings.corridorRoomFactor = action.payload;
    },
    setAutoPipeRoutingBlockedRoomFactorInRedux(state, action: PayloadAction<number>) {
      state.autoPipeRoutingCostSettings.blockedRoomFactor = action.payload;
    },
    setAutoPipeRoutingVerticalPenaltyFactorInRedux(state, action: PayloadAction<number>) {
      state.autoPipeRoutingCostSettings.verticalPenaltyFactor = action.payload;
    },
    setAutoPipeRoutingPerimeterPenaltyFactorInRedux(state, action: PayloadAction<number>) {
      state.autoPipeRoutingCostSettings.perimeterPenaltyFactor = action.payload;
    },
    setAutoPipeRoutingCostSettingsInRedux(
      state,
      action: PayloadAction<IAutoPipeRoutingCostSettings>
    ) {
      state.autoPipeRoutingCostSettings = action.payload;
    },
    setAutoPipeRoutingRunStateInRedux(
      state,
      action: PayloadAction<IPipeRoutingSliceState["autoPipeRoutingRunState"]>
    ) {
      state.autoPipeRoutingRunState = action.payload;
    },
    clearAutoPipeRoutingRunStateInRedux(state) {
      state.autoPipeRoutingRunState = {
        isPreparing: false,
        isRunning: false,
        currentIteration: 0,
        totalIterations: 0,
      };
    },
  },
});

export const {
  setAutoPipeRoutingModalOpenInRedux,
  setAutoPipeRoutingPickingConnectionPointInViewerInRedux,
  setAutoPipeRoutingWizardStepInRedux,
  setSelectedAutoPipeRoutingSystemTypeInRedux,
  clearSelectedAutoPipeRoutingSystemTypeInRedux,
  setAutoPipeRoutingPipeElevationsMmAboveFloorForSystemInRedux,
  setAutoPipeRoutingConnectionPointInRedux,
  addAutoPipeRoutingConnectionPointInRedux,
  setAutoPipeRoutingPipeSeriesNameInRedux,
  setAutoPipeRoutingPipeDimensionInRedux,
  setAutoPipeRoutingRoomCrossingPenaltyMInRedux,
  setAutoPipeRoutingCorridorRoomFactorInRedux,
  setAutoPipeRoutingBlockedRoomFactorInRedux,
  setAutoPipeRoutingVerticalPenaltyFactorInRedux,
  setAutoPipeRoutingPerimeterPenaltyFactorInRedux,
  setAutoPipeRoutingCostSettingsInRedux,
  setAutoPipeRoutingRunStateInRedux,
  clearAutoPipeRoutingRunStateInRedux,
} = pipeRoutingSlice.actions;

export const selectPipeRoutingState = (state: IRootState): IPipeRoutingSliceState =>
  state.pipeRouting;

export const selectAutoPipeRoutingModalOpen = (state: IRootState): boolean =>
  state.pipeRouting.autoPipeRoutingModalOpen;

export const selectAutoPipeRoutingPickingConnectionPointInViewer = (state: IRootState): boolean =>
  state.pipeRouting.autoPipeRoutingPickingConnectionPointInViewer;

export const selectAutoPipeRoutingWizardStep = (state: IRootState): IAutoPipeRoutingWizardStep =>
  state.pipeRouting.autoPipeRoutingWizardStep;

export const selectSelectedAutoPipeRoutingSystemType = (
  state: IRootState
): IAutoPipeRoutingSystemType | null => state.pipeRouting.selectedAutoPipeRoutingSystemType;

export const selectAutoPipeRoutingPipeElevationsMmAboveFloorBySystem = (
  state: IRootState
): Partial<
  Record<
    IAutoPipeRoutingSystemType,
    {
      xDirectionMmAboveFloor: number;
      yDirectionMmAboveFloor: number;
    }
  >
> => state.pipeRouting.autoPipeRoutingPipeElevationsMmAboveFloorBySystem;

export const selectAutoPipeRoutingConnectionPoints = (
  state: IRootState
): IAutoPipeRoutingConnectionPoint[] => state.pipeRouting.autoPipeRoutingConnectionPoints;
export const selectAutoPipeRoutingPipeSeriesName = (state: IRootState): string | null =>
  state.pipeRouting.autoPipeRoutingPipeSeriesName;
export const selectAutoPipeRoutingPipeDimension = (state: IRootState): number | null =>
  state.pipeRouting.autoPipeRoutingPipeDimension;

export const selectAutoPipeRoutingCostSettings = (
  state: IRootState
): IAutoPipeRoutingCostSettings => state.pipeRouting.autoPipeRoutingCostSettings;

export const selectAutoPipeRoutingRoomCrossingPenaltyM = (state: IRootState): number =>
  state.pipeRouting.autoPipeRoutingCostSettings.roomCrossingPenaltyM;

export const selectAutoPipeRoutingCorridorRoomFactor = (state: IRootState): number =>
  state.pipeRouting.autoPipeRoutingCostSettings.corridorRoomFactor;

export const selectAutoPipeRoutingBlockedRoomFactor = (state: IRootState): number =>
  state.pipeRouting.autoPipeRoutingCostSettings.blockedRoomFactor;

export const selectAutoPipeRoutingVerticalPenaltyFactor = (state: IRootState): number =>
  state.pipeRouting.autoPipeRoutingCostSettings.verticalPenaltyFactor;

export const selectAutoPipeRoutingPerimeterPenaltyFactor = (state: IRootState): number =>
  state.pipeRouting.autoPipeRoutingCostSettings.perimeterPenaltyFactor;

export const selectAutoPipeRoutingRunState = (
  state: IRootState
): IPipeRoutingSliceState["autoPipeRoutingRunState"] => state.pipeRouting.autoPipeRoutingRunState;

export const selectAutoPipeRoutingIsPreparing = (state: IRootState): boolean =>
  state.pipeRouting.autoPipeRoutingRunState.isPreparing;

export const selectAutoPipeRoutingIsRunning = (state: IRootState): boolean =>
  state.pipeRouting.autoPipeRoutingRunState.isRunning;

export const selectShouldShowAutoPipeRoutingRunProgress = (state: IRootState): boolean =>
  state.pipeRouting.autoPipeRoutingRunState.isPreparing ||
  (state.pipeRouting.autoPipeRoutingRunState.isRunning &&
    state.pipeRouting.autoPipeRoutingRunState.totalIterations > 0);

export default pipeRoutingSlice.reducer;
